import dchAPI from '@/services/dchAPI';

const state = {
  // Chat state
  currentSessionId: null,
  messages: [],
  isProcessing: false,

  // Sessions list
  sessions: [],
  sessionsCursor: null,
  isLoadingSessions: false,
  isLoadingMoreSessions: false,

  // Draft preservation per session
  sessionDrafts: {},

  // Templates
  templates: [],
  isLoadingTemplates: false,
  currentTemplate: null,
  templateResult: null,
  isRunningTemplate: false,

  // Usage
  usageSummary: null,
  usageRecords: [],
  isLoadingUsage: false,
};

const getters = {
  currentSessionId: (state) => state.currentSessionId,
  messages: (state) => state.messages,
  isProcessing: (state) => state.isProcessing,
  sessions: (state) => state.sessions,
  sessionsCursor: (state) => state.sessionsCursor,
  hasMoreSessions: (state) => !!state.sessionsCursor,
  isLoadingSessions: (state) => state.isLoadingSessions,
  isLoadingMoreSessions: (state) => state.isLoadingMoreSessions,
  getSessionDraft: (state) => (sessionId) => state.sessionDrafts[sessionId] || '',
  templates: (state) => state.templates,
  isLoadingTemplates: (state) => state.isLoadingTemplates,
  currentTemplate: (state) => state.currentTemplate,
  templateResult: (state) => state.templateResult,
  isRunningTemplate: (state) => state.isRunningTemplate,
  usageSummary: (state) => state.usageSummary,
  usageRecords: (state) => state.usageRecords,
  isLoadingUsage: (state) => state.isLoadingUsage,
};

const mutations = {
  SET_CURRENT_SESSION_ID(state, sessionId) {
    state.currentSessionId = sessionId;
  },
  SET_MESSAGES(state, messages) {
    state.messages = messages;
  },
  ADD_MESSAGE(state, message) {
    state.messages.push(message);
  },
  SET_IS_PROCESSING(state, isProcessing) {
    state.isProcessing = isProcessing;
  },
  SET_SESSIONS(state, { sessions, cursor }) {
    state.sessions = sessions;
    state.sessionsCursor = cursor || null;
  },
  APPEND_SESSIONS(state, { sessions, cursor }) {
    state.sessions = [...state.sessions, ...sessions];
    state.sessionsCursor = cursor || null;
  },
  SET_IS_LOADING_SESSIONS(state, isLoading) {
    state.isLoadingSessions = isLoading;
  },
  SET_IS_LOADING_MORE_SESSIONS(state, isLoading) {
    state.isLoadingMoreSessions = isLoading;
  },
  SET_TEMPLATES(state, templates) {
    state.templates = templates;
  },
  SET_IS_LOADING_TEMPLATES(state, isLoading) {
    state.isLoadingTemplates = isLoading;
  },
  SET_CURRENT_TEMPLATE(state, template) {
    state.currentTemplate = template;
  },
  SET_TEMPLATE_RESULT(state, result) {
    state.templateResult = result;
  },
  SET_IS_RUNNING_TEMPLATE(state, isRunning) {
    state.isRunningTemplate = isRunning;
  },
  CLEAR_CHAT(state) {
    state.currentSessionId = null;
    state.messages = [];
  },
  SET_SESSION_DRAFT(state, { sessionId, text }) {
    state.sessionDrafts[sessionId] = text;
  },
  REMOVE_SESSION(state, sessionId) {
    state.sessions = state.sessions.filter((s) => s.session_id !== sessionId);
  },
  CLEAR_TEMPLATE_RESULT(state) {
    state.templateResult = null;
  },
  SET_USAGE_SUMMARY(state, summary) {
    state.usageSummary = summary;
  },
  SET_USAGE_RECORDS(state, records) {
    state.usageRecords = records;
  },
  SET_IS_LOADING_USAGE(state, isLoading) {
    state.isLoadingUsage = isLoading;
  },
};

const actions = {
  async sendMessage({ commit, state, dispatch }, message) {
    commit('SET_IS_PROCESSING', true);

    // Add user message to UI immediately
    commit('ADD_MESSAGE', {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await dchAPI.post('/ai/chat', {
        session_id: state.currentSessionId,
        message: message,
      });

      const { session_id } = response.data.data;

      // Update session ID if new
      if (!state.currentSessionId) {
        commit('SET_CURRENT_SESSION_ID', session_id);
        dispatch('fetchSessions');
      }

      // Reload full session to get all messages (including intermediate assistant messages)
      // This ensures consistency between real-time and loaded sessions
      const sessionResponse = await dchAPI.get(`/ai/sessions/${session_id}`);
      commit('SET_MESSAGES', sessionResponse.data.data.messages || []);

      return response.data.data;
    } finally {
      commit('SET_IS_PROCESSING', false);
    }
  },

  async sendMessageAsync({ commit, state, dispatch }, message) {
    commit('SET_IS_PROCESSING', true);

    // Add user message to UI immediately
    commit('ADD_MESSAGE', {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    });

    try {
      // Submit async chat request
      const response = await dchAPI.post('/ai/chat/async', {
        session_id: state.currentSessionId,
        message: message,
      });

      const { job_id, session_id } = response.data.data;

      // Update session ID if new
      if (!state.currentSessionId) {
        commit('SET_CURRENT_SESSION_ID', session_id);
        dispatch('fetchSessions');
      }

      // Poll for completion
      await dispatch('pollChatJob', { jobId: job_id, sessionId: session_id });

      return response.data.data;
    } finally {
      commit('SET_IS_PROCESSING', false);
    }
  },

  async pollChatJob({ commit }, { jobId, sessionId }) {
    const POLL_INTERVAL = 2000;
    const MAX_POLLS = 150; // 5 minutes max

    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));

      // Check job status
      const statusResponse = await dchAPI.get(`/ai/chat/jobs/${jobId}`);
      const { status } = statusResponse.data.data;

      if (status === 'running' || status === 'completed') {
        // Fetch latest messages from session
        const sessionResponse = await dchAPI.get(`/ai/sessions/${sessionId}`);
        commit('SET_MESSAGES', sessionResponse.data.data.messages || []);
      }

      if (status === 'completed') {
        return;
      }

      if (status === 'failed') {
        const { error } = statusResponse.data.data;
        throw new Error(error || 'AI chat processing failed');
      }
    }

    throw new Error('AI chat timed out');
  },

  async sendTemplateMessage({ commit, state, dispatch }, { templateName, parameters, filledPrompt }) {
    commit('SET_IS_PROCESSING', true);

    // Add user message with template metadata to UI immediately
    commit('ADD_MESSAGE', {
      role: 'user',
      content: filledPrompt,
      template: true,
      template_name: templateName,
      parameters: parameters,
      timestamp: new Date().toISOString(),
    });

    try {
      // Submit async chat request
      const response = await dchAPI.post('/ai/chat/async', {
        session_id: state.currentSessionId,
        message: filledPrompt,
      });

      const { job_id, session_id } = response.data.data;

      // Update session ID if new
      if (!state.currentSessionId) {
        commit('SET_CURRENT_SESSION_ID', session_id);
        dispatch('fetchSessions');
      }

      // Poll for completion
      await dispatch('pollChatJob', { jobId: job_id, sessionId: session_id });

      return response.data.data;
    } finally {
      commit('SET_IS_PROCESSING', false);
    }
  },

  async fetchSessions({ commit }, { limit = 20 } = {}) {
    commit('SET_IS_LOADING_SESSIONS', true);
    try {
      const response = await dchAPI.get('/ai/sessions', { params: { limit } });
      commit('SET_SESSIONS', {
        sessions: response.data.data,
        cursor: response.data.meta?.cursor,
      });
      return response.data.data;
    } finally {
      commit('SET_IS_LOADING_SESSIONS', false);
    }
  },

  async fetchMoreSessions({ commit, state }, { limit = 20 } = {}) {
    if (!state.sessionsCursor) return [];

    commit('SET_IS_LOADING_MORE_SESSIONS', true);
    try {
      const response = await dchAPI.get('/ai/sessions', {
        params: { limit, cursor: state.sessionsCursor },
      });
      commit('APPEND_SESSIONS', {
        sessions: response.data.data,
        cursor: response.data.meta?.cursor,
      });
      return response.data.data;
    } finally {
      commit('SET_IS_LOADING_MORE_SESSIONS', false);
    }
  },

  async loadSession({ commit }, sessionId) {
    const response = await dchAPI.get(`/ai/sessions/${sessionId}`);
    const session = response.data.data;
    commit('SET_CURRENT_SESSION_ID', session.session_id);
    commit('SET_MESSAGES', session.messages || []);
    return session;
  },

  async deleteSession({ commit, state, dispatch }, sessionId) {
    await dchAPI.delete(`/ai/sessions/${sessionId}`);
    commit('REMOVE_SESSION', sessionId);
    // If we deleted the current session, clear the chat
    if (state.currentSessionId === sessionId) {
      commit('CLEAR_CHAT');
    }
  },

  startNewChat({ commit }) {
    commit('CLEAR_CHAT');
  },

  setSessionDraft({ commit }, { sessionId, text }) {
    commit('SET_SESSION_DRAFT', { sessionId, text });
  },

  // Template actions
  async fetchTemplates({ commit }, visibility = 'all') {
    commit('SET_IS_LOADING_TEMPLATES', true);
    try {
      const response = await dchAPI.get('/ai/templates', {
        params: { visibility },
      });
      commit('SET_TEMPLATES', response.data.data);
      return response.data.data;
    } finally {
      commit('SET_IS_LOADING_TEMPLATES', false);
    }
  },

  async fetchTemplate({ commit }, templateId) {
    const response = await dchAPI.get(`/ai/templates/${templateId}`);
    commit('SET_CURRENT_TEMPLATE', response.data.data);
    return response.data.data;
  },

  async createTemplate({ dispatch }, templateData) {
    const response = await dchAPI.post('/ai/templates', templateData);
    await dispatch('fetchTemplates');
    return response.data.data;
  },

  async updateTemplate({ dispatch }, { templateId, updates }) {
    const response = await dchAPI.put(`/ai/templates/${templateId}`, updates);
    await dispatch('fetchTemplates');
    return response.data.data;
  },

  async deleteTemplate({ dispatch }, templateId) {
    await dchAPI.delete(`/ai/templates/${templateId}`);
    await dispatch('fetchTemplates');
  },

  async runTemplate({ commit, dispatch }, { templateId, parameters }) {
    commit('SET_IS_RUNNING_TEMPLATE', true);
    try {
      const response = await dchAPI.post(`/ai/templates/${templateId}/run`, {
        parameters,
      });

      const result = response.data.data;

      // If backend returns a session, load it
      if (result.session_id) {
        commit('SET_CURRENT_SESSION_ID', result.session_id);
        commit('SET_MESSAGES', result.messages || []);
        // Refresh sessions list to include new session
        dispatch('fetchSessions');
      } else {
        // Fallback: store result for modal display (backwards compatibility)
        commit('SET_TEMPLATE_RESULT', result);
      }

      return result;
    } finally {
      commit('SET_IS_RUNNING_TEMPLATE', false);
    }
  },

  clearTemplateResult({ commit }) {
    commit('CLEAR_TEMPLATE_RESULT');
  },

  // Usage actions
  async fetchUsageSummary({ commit }, { start, end } = {}) {
    commit('SET_IS_LOADING_USAGE', true);
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const response = await dchAPI.get('/ai/usage/summary', { params });
      commit('SET_USAGE_SUMMARY', response.data.data);
      return response.data.data;
    } finally {
      commit('SET_IS_LOADING_USAGE', false);
    }
  },

  async fetchUsageRecords({ commit }, { start, end, principal, limit = 100 } = {}) {
    commit('SET_IS_LOADING_USAGE', true);
    try {
      const params = { limit };
      if (start) params.start = start;
      if (end) params.end = end;
      if (principal) params.principal = principal;
      const response = await dchAPI.get('/ai/usage', { params });
      commit('SET_USAGE_RECORDS', response.data.data);
      return response.data.data;
    } finally {
      commit('SET_IS_LOADING_USAGE', false);
    }
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
